/**
 * @description
 * Anthropic management page for organization info, workspace management,
 * API keys, usage reporting, and cost analysis.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 10, 2025
 * @see /admin/src/frontend/pages/Anthropic.test.tsx
 */

import { useAnthropicData } from "./Anthropic/hooks/useAnthropicData";
import { OrganizationOverview } from "./Anthropic/OrganizationOverview";
import { WorkspaceManager } from "./Anthropic/WorkspaceManager";
import { ApiKeyList } from "./Anthropic/ApiKeyList";
import { UsageReports } from "./Anthropic/UsageReports";
import { CostReports } from "./Anthropic/CostReports";

/**
 * Anthropic management page component
 */
export function Anthropic() {
  const {
    activeTab,
    loading,
    organization,
    workspaces,
    clients,
    showCreateWorkspace,
    newWorkspaceName,
    assigningClients,
    selectedClients,
    apiKeys,
    usageData,
    usageStartingAt,
    usageEndingAt,
    usageBucketWidth,
    costData,
    costStartingAt,
    costEndingAt,
    costBucketWidth,
    setActiveTab,
    setShowCreateWorkspace,
    setNewWorkspaceName,
    setAssigningClients,
    setSelectedClients,
    setUsageStartingAt,
    setUsageEndingAt,
    setUsageBucketWidth,
    setCostStartingAt,
    setCostEndingAt,
    setCostBucketWidth,
    fetchOrganization,
    fetchWorkspaces,
    fetchApiKeys,
    fetchUsage,
    fetchCost,
    handleCreateWorkspace,
    handleArchiveWorkspace,
    handleAssignClients,
  } = useAnthropicData();

  const handleRefresh = () => {
    if (activeTab === "organization") {
      fetchOrganization();
      fetchWorkspaces();
      fetchApiKeys();
    } else if (activeTab === "workspaces") {
      fetchWorkspaces();
    } else if (activeTab === "api-keys") {
      fetchApiKeys();
      fetchWorkspaces();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "organization":
        return (
          <OrganizationOverview
            organization={organization}
            workspaces={workspaces}
            apiKeys={apiKeys}
            usageData={usageData}
            loading={loading}
            onRefresh={handleRefresh}
          />
        );
      
      case "workspaces":
        return (
          <WorkspaceManager
            workspaces={workspaces}
            clients={clients}
            showCreateWorkspace={showCreateWorkspace}
            newWorkspaceName={newWorkspaceName}
            assigningClients={assigningClients}
            selectedClients={selectedClients}
            loading={loading}
            onRefresh={handleRefresh}
            onCreateWorkspace={handleCreateWorkspace}
            onArchiveWorkspace={handleArchiveWorkspace}
            onAssignClients={handleAssignClients}
            onShowCreateWorkspace={setShowCreateWorkspace}
            onSetNewWorkspaceName={setNewWorkspaceName}
            onSetAssigningClients={setAssigningClients}
            onSetSelectedClients={setSelectedClients}
          />
        );
      
      case "api-keys":
        return (
          <ApiKeyList
            apiKeys={apiKeys}
            workspaces={workspaces}
            loading={loading}
            onRefresh={handleRefresh}
          />
        );
      
      case "usage":
        return (
          <UsageReports
            usageData={usageData}
            usageStartingAt={usageStartingAt}
            usageEndingAt={usageEndingAt}
            usageBucketWidth={usageBucketWidth}
            workspaces={workspaces}
            loading={loading}
            onSetUsageStartingAt={setUsageStartingAt}
            onSetUsageEndingAt={setUsageEndingAt}
            onSetUsageBucketWidth={setUsageBucketWidth}
            onFetchUsage={fetchUsage}
          />
        );
      
      case "cost":
        return (
          <CostReports
            costData={costData}
            costStartingAt={costStartingAt}
            costEndingAt={costEndingAt}
            costBucketWidth={costBucketWidth}
            workspaces={workspaces}
            loading={loading}
            onSetCostStartingAt={setCostStartingAt}
            onSetCostEndingAt={setCostEndingAt}
            onSetCostBucketWidth={setCostBucketWidth}
            onFetchCost={fetchCost}
          />
        );
      
      default:
        return null;
    }
  };

  if (
    loading &&
    !organization &&
    !workspaces.length &&
    !usageData.length &&
    !apiKeys.length
  ) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Anthropic Management
        </h1>
        <p className="text-muted-foreground">
          Manage your Anthropic organization, workspaces, and usage
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("organization")}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors ${
              activeTab === "organization"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground"
            }`}
          >
            Organization
          </button>
          <button
            onClick={() => setActiveTab("workspaces")}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors ${
              activeTab === "workspaces"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground"
            }`}
          >
            Workspaces
          </button>
          <button
            onClick={() => setActiveTab("api-keys")}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors ${
              activeTab === "api-keys"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground"
            }`}
          >
            API Keys
          </button>
          <button
            onClick={() => setActiveTab("usage")}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors ${
              activeTab === "usage"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground"
            }`}
          >
            Usage
          </button>
          <button
            onClick={() => setActiveTab("cost")}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors ${
              activeTab === "cost"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground"
            }`}
          >
            Cost
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="rounded-lg border bg-card p-6">
        {renderTabContent()}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */